import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { ResolvedContentReference } from '@gitbook/app-database';
import { useHiveBranch, useHiveClient, useHiveDataSyncClock } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';
import { useEventCallback, useLiveRef, usePrevious } from '@gitbook/util-react-hooks';

import { useNavigation } from '../../navigation';
import { FileIcon } from '../Files';
import { PageIcon } from '../Page';
import { SpaceIcon } from '../Space';
import { UserAvatar } from '../Users';
import { type ResolutionOptions, resolveContentRefCache } from './caches';
import { useContentReferenceContext } from './context';
import type { UIResolvedContentReference } from './types';

/**
 * Resolve content references to a metadata to render in the UI.
 *
 * It relies on a ContentRefContext being provided at a higher level via a React context.
 */
export function useContentReferences(
    contentRefs: api.ContentRef[],
    options?: ResolutionOptions
): UIResolvedContentReference[] {
    const { navigate, getNavigationURL } = useNavigation();
    const hiveClient = useHiveClient();
    const context = useContentReferenceContext();

    const resolutionInputs = contentRefs.map(
        (contentRef) => [hiveClient, contentRef, context, options ?? {}] as const
    );

    const allResolved = resolveContentRefCache.useValues(resolutionInputs);

    // Use the HiveBranch clock as a cache key, to ensure we invalidate
    // the cache when the revision mutates.
    const hiveBranch = useHiveBranch();
    const hiveBranchClock = useHiveDataSyncClock(hiveBranch?.dataSync);

    const cacheKey = `${hiveBranchClock ?? ''}`;
    const previousCacheKey = usePrevious(cacheKey);

    // Refetch the content refererences when the cache key changes
    const resolutionInputsRef = useLiveRef(resolutionInputs);
    React.useEffect(() => {
        if (previousCacheKey !== undefined && previousCacheKey !== cacheKey) {
            const inputs = resolutionInputsRef.current;

            // Refresh the cache for all the inputs
            inputs.forEach((input) => {
                resolveContentRefCache.fetch(input);
            });
        }
    }, [cacheKey, previousCacheKey, resolutionInputsRef]);

    return React.useMemo(() => {
        return allResolved.map(([resolved, permissionsDenied], index) => {
            const contentRef = contentRefs[index];
            if (!resolved) {
                return {
                    title: permissionsDenied ? 'Access denied' : 'Broken link',
                    icon: Icon.LinkBroken,
                    secondaryIcon: Icon.LinkBroken,
                    href: '',
                    open: () => {},
                };
            }

            return {
                title: resolved.text,
                icon: createIconForContentRef(contentRef, resolved),
                secondaryIcon: createSecondaryIconForContentRef(contentRef),
                secondaryIconDescription: createIconDescriptionContentRef(contentRef),
                href:
                    // https://linear.app/gitbook-x/issue/RND-1639/remove-ability-to-view-users-in-comments
                    contentRef.kind === 'user'
                        ? ''
                        : typeof resolved.target === 'string'
                          ? resolved.target
                          : getNavigationURL({ screen: resolved.target }),
                open: () => {
                    // https://linear.app/gitbook-x/issue/RND-1639/remove-ability-to-view-users-in-comments
                    if (contentRef.kind === 'user') {
                        return;
                    }

                    if (typeof resolved.target === 'string') {
                        window.open(resolved.target, '_blank');
                    } else {
                        navigate({ screen: resolved.target });
                    }
                },
                resolution: resolved,
            };
        });
    }, [getNavigationURL, navigate, contentRefs, allResolved]);
}

/**
 * Clears the content reference cache for the current context.
 */
export function useClearContentReferenceCache(options?: ResolutionOptions) {
    const hiveClient = useHiveClient();
    const context = useContentReferenceContext();
    return useEventCallback((contentRef: api.ContentRef) => {
        resolveContentRefCache.clear([hiveClient, contentRef, context, options]);
    });
}

/**
 * Returns a callback to resolve a content reference using the current context.
 */
export function useResolveContentReference(options?: ResolutionOptions) {
    const hiveClient = useHiveClient();
    const context = useContentReferenceContext();

    return useEventCallback((contentRef: api.ContentRef) =>
        resolveContentRefCache.readAsync([hiveClient, contentRef, context, options])
    );
}

/**
 * Resolve a content reference to an UI representation.
 */
export function useContentReference(
    contentRef: api.ContentRef | undefined | null,
    options?: ResolutionOptions
): UIResolvedContentReference {
    const result = useContentReferences(contentRef ? [contentRef] : [], options);

    return (
        result[0] || {
            title: 'Broken link',
            icon: Icon.LinkBroken,
            secondaryIcon: Icon.LinkBroken,
            href: '',
            open: () => {},
        }
    );
}

function createIconForContentRef(
    contentRef: api.ContentRef,
    resolved: ResolvedContentReference<api.Space>
): IconDefinition | undefined {
    if (contentRef.kind === 'url') {
        return Icon.Link;
    }

    if (typeof resolved.target !== 'string' && resolved.target.type === 'user') {
        return <UserAvatar size="xs" user={resolved.target.user} />;
    }

    if (resolved.extra?.file) {
        return <FileIcon file={resolved.extra.file} />;
    }

    if (resolved.extra?.space) {
        return <SpaceIcon space={resolved.extra.space} />;
    }

    if (resolved.extra?.page && (resolved.extra.page.emoji || resolved.extra.page.icon)) {
        return <PageIcon page={resolved.extra.page} />;
    }

    return undefined;
}

function createSecondaryIconForContentRef(contentRef: api.ContentRef) {
    switch (contentRef.kind) {
        case 'user':
            return Icon.AtSign;
        case 'anchor':
            return Icon.Hash;
        case 'page':
            return Icon.PageLink;
        case 'space':
            return Icon.SpaceLink;
        case 'collection':
            return Icon.CollectionLink;
        case 'url':
            return Icon.Link;
    }
}

function createIconDescriptionContentRef(contentRef: api.ContentRef): string | undefined {
    switch (contentRef.kind) {
        case 'user':
            return 'User mention';
        case 'anchor':
            return `Link to a page's anchor`;
        case 'page':
            return 'Link to a page';
        case 'space':
            return 'Link to a space';
        case 'collection':
            return 'Link to a acollection';
        case 'url':
            return 'External URL';
    }
}
